// 통화 내역 화면 포맷/상태 헬퍼 (sms.tsx 의 모듈 로컬 포맷터를 복사 — export 되어 있지 않음)

// "YYYY-MM-DD HH:mm:ss" (서버 KST 문자열) → epoch ms (브라우저 로컬 = KST 전제)
export const parseLocal = (s : string | null | undefined) : number | null => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime();
};

//전화번호 보기 좋게 (서버 전송/조회는 항상 raw)
export const formatPhone = (p : string) => {
    if (!p) return "";
    const d = ("" + p).replace(/[^0-9]/g, "");
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) {
        if (d.startsWith("02")) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
        return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return p;
};

//"YYYY-MM-DD HH:mm:ss" → "오전/오후 h:mm"
export const formatClock = (s : string) => {
    if (!s) return "";
    const time = (s.split(" ")[1]) || "";
    const [hh, mm] = time.split(":");
    if (hh === undefined || mm === undefined) return "";
    const h = +hh;
    const ampm = h < 12 ? "오전" : "오후";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${ampm} ${h12}:${mm}`;
};

//"HH:mm" (24시간)
export const formatHm = (s : string) => {
    if (!s) return "";
    const time = (s.split(" ")[1]) || "";
    return time.slice(0, 5);
};

//목록 시각(카카오톡식): 오늘이면 시계, 어제면 "어제", 같은 해면 "M/D", 이전 해면 "YYYY. M. D."
export const formatRoomTime = (s : string) => {
    if (!s) return "";
    const datePart = (s.split(" ")[0]) || "";
    const parts = datePart.split("-");
    if (parts.length < 3) return datePart;
    const y = +parts[0], m = +parts[1], d = +parts[2];
    const now = new Date();
    if (y === now.getFullYear() && m === now.getMonth() + 1 && d === now.getDate()) return formatHm(s);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (y === yesterday.getFullYear() && m === yesterday.getMonth() + 1 && d === yesterday.getDate()) return "어제";
    if (y === now.getFullYear()) return `${m}/${d}`;
    return `${y}. ${m}. ${d}.`;
};

//우측 통화 행 일시: 오늘 "오늘 12:58", 어제 "어제 16:44", 그 외 "8/21 16:44"
export const formatCallTime = (s : string) => {
    if (!s) return "";
    const datePart = (s.split(" ")[0]) || "";
    const parts = datePart.split("-");
    if (parts.length < 3) return datePart;
    const y = +parts[0], m = +parts[1], d = +parts[2];
    const now = new Date();
    if (y === now.getFullYear() && m === now.getMonth() + 1 && d === now.getDate()) return `오늘 ${formatHm(s)}`;
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (y === yesterday.getFullYear() && m === yesterday.getMonth() + 1 && d === yesterday.getDate()) return `어제 ${formatHm(s)}`;
    if (y === now.getFullYear()) return `${m}/${d} ${formatHm(s)}`;
    return `${y}. ${m}. ${d}. ${formatHm(s)}`;
};

//"M/D"
export const formatMd = (s : string) => {
    if (!s) return "";
    const parts = ((s.split(" ")[0]) || "").split("-");
    if (parts.length < 3) return "";
    return `${+parts[1]}/${+parts[2]}`;
};

//날짜 구분선: "2026년 7월 9일 목요일"
const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
export const formatDateDivider = (s : string) => {
    if (!s) return "";
    const parts = ((s.split(" ")[0]) || "").split("-");
    if (parts.length < 3) return "";
    const y = +parts[0], m = +parts[1], d = +parts[2];
    return `${y}년 ${m}월 ${d}일 ${dayNames[new Date(y, m - 1, d).getDay()]}요일`;
};

//경과: <1분 "방금", <60분 "N분", <24시간 "N시간", 그 외 "N일"
export const formatElapsed = (fromIso : string | null | undefined, nowMs : number, toIso? : string | null) => {
    const from = parseLocal(fromIso);
    if (from === null) return "";
    const to = toIso ? (parseLocal(toIso) || nowMs) : nowMs;
    const sec = Math.max(0, Math.floor((to - from) / 1000));
    if (sec < 60) return "방금";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}분`;
    const hour = Math.floor(min / 60);
    if (hour < 24) return `${hour}시간`;
    return `${Math.floor(hour / 24)}일`;
};

//통화 길이 "M분 S초" / "S초"
export const formatTalk = (sec : number | null | undefined) => {
    if (sec === null || sec === undefined) return "";
    const s = Math.max(0, Math.floor(sec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m > 0 ? `${m}분 ${r}초` : `${r}초`;
};

export type StatusKind = "hidden" | "incall" | "callback" | "manual" | "done" | "noanswer" | "waiting" | "none";

export interface ContactStatus {
    kind : StatusKind;
    line1 : string;
    line2 : string;
    underline? : boolean;
}

//좌측 표 상태 셀 규칙 (우선순위 순)
export const statusOf = (c : any, nowMs : number, formatDuration : (sec : number) => string) : ContactStatus => {
    if (c.hidden) {
        return { kind : "hidden", line1 : "숨김", line2 : "" };
    }
    if (c.inCallSince) {
        const since = parseLocal(c.inCallSince);
        const sec = since === null ? 0 : Math.max(0, Math.floor((nowMs - since) / 1000));
        return { kind : "incall", line1 : `통화중 ${formatDuration(sec)}`, line2 : c.inCallAgent || "" };
    }
    if (c.callbackNeeded) {
        return { kind : "callback", line1 : `부재중 ${formatElapsed(c.callbackSince, nowMs)} 경과`, line2 : c.lastQueueName || "" };
    }
    if (c.manualDone) {
        //처리 시점까지의 경과로 고정 + 밑줄 "_수동처리"
        return { kind : "manual", line1 : `부재중 ${formatElapsed(c.lastMissedAt, nowMs, c.manualDoneAt)}_수동처리`, line2 : c.lastQueueName || "", underline : true };
    }
    switch (c.lastResult) {
        case "callback_done":
            return { kind : "done", line1 : "콜백 완료", line2 : c.lastAgentName || "" };
        case "connected":
            return { kind : "done", line1 : "응대 완료", line2 : c.lastQueueName || c.lastAgentName || "" };
        case "outbound_connected":
            return { kind : "done", line1 : "응대 완료", line2 : c.lastAgentName ? `발신 · ${c.lastAgentName}` : "발신" };
        case "outbound_noanswer":
            return { kind : "noanswer", line1 : "발신 부재", line2 : c.lastAgentName || "" };
        case "missed":
            return { kind : "noanswer", line1 : "부재중", line2 : c.lastQueueName || "" };
        case "waiting":
            return { kind : "waiting", line1 : "대기중", line2 : c.lastQueueName || "" };
        default:
            return { kind : "none", line1 : "—", line2 : "" };
    }
};

//서버 ORDER BY 와 동일한 정렬 (소켓 diff 반영 후 재정렬용)
export const compareContacts = (a : any, b : any) => {
    if ((a.hidden ? 1 : 0) !== (b.hidden ? 1 : 0)) return (a.hidden ? 1 : 0) - (b.hidden ? 1 : 0);
    if ((a.callbackNeeded ? 1 : 0) !== (b.callbackNeeded ? 1 : 0)) return (b.callbackNeeded ? 1 : 0) - (a.callbackNeeded ? 1 : 0);
    if (a.callbackNeeded && b.callbackNeeded) {
        const sa = a.callbackSince || "", sb = b.callbackSince || "";
        if (sa !== sb) return sa < sb ? -1 : 1;
    }
    if ((a.manualDone ? 1 : 0) !== (b.manualDone ? 1 : 0)) return (b.manualDone ? 1 : 0) - (a.manualDone ? 1 : 0);
    const la = a.lastContactAt || "", lb = b.lastContactAt || "";
    if (la !== lb) return la < lb ? 1 : -1;
    return 0;
};

//필터/검색 조건 (서버와 동일)
export const matchesFilter = (c : any, filter : string) => {
    if (filter === "callback") return !c.hidden && !!c.callbackNeeded;
    if (filter === "today") {
        if (c.hidden || !c.lastContactAt) return false;
        const now = new Date();
        const p = (n : number) => (n < 10 ? "0" + n : "" + n);
        const today = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} 00:00:00`;
        return c.lastContactAt >= today;
    }
    return true;
};

export const matchesSearch = (c : any, search : string) => {
    const s = (search || "").trim();
    if (!s) return true;
    const digits = s.replace(/[^0-9]/g, "");
    if (digits && digits.length === s.replace(/[-\s]/g, "").length) {
        return String(c.phoneNumber || "").indexOf(digits) !== -1;
    }
    return String(c.displayName || "").indexOf(s) !== -1;
};
